import React from 'react';
import { motion } from 'framer-motion';
import { PieChart, Camera, Handshake, Key } from 'lucide-react';
import { CheckCircle2 } from 'lucide-react';

const ProcessSection = () => {
  return (
    <section id="come-funziona" className="py-24 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <span className="text-secondary uppercase tracking-wider font-semibold">Il Nostro Processo</span>
          <h2 className="text-4xl font-monomakh text-primary mt-2 mb-4">Come Lavoriamo</h2>
          <p className="text-gray-600 text-lg max-w-3xl mx-auto">
            Il nostro processo è progettato per assicurarti la massima tranquillità e
            i migliori risultati in ogni fase della vendita del tuo immobile.
          </p>
        </div>
        
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-primary/20 hidden md:block"></div>
          
          {/* Timeline steps */}
          <div className="space-y-24 relative">
            {/* Step 1 */}
            <motion.div 
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="grid md:grid-cols-2 gap-16 items-center"
            >
              <div className="md:text-right order-2 md:order-1">
                <div className="inline-block bg-primary/10 px-3 py-1 rounded text-primary text-sm font-semibold mb-3">
                  Passo 1
                </div>
                <h3 className="text-2xl font-semibold mb-4 text-primary">Consulenza Iniziale e Valutazione</h3>
                <p className="text-gray-600 text-lg mb-4">
                  Il nostro team di esperti analizza il tuo immobile e il mercato locale, offrendo una
                  valutazione gratuita e senza impegno basata su dati concreti e conoscenza approfondita
                </p>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-center md:justify-end">
                    <span>Analisi di immobili comparabili</span>
                    <CheckCircle2 className="md:order-1 h-5 w-5 text-secondary ml-0 md:ml-2 mr-2 md:mr-0" />
                  </li>
                  <li className="flex items-center md:justify-end">
                    <span>Studio delle tendenze di mercato</span>
                    <CheckCircle2 className="md:order-1 h-5 w-5 text-secondary ml-0 md:ml-2 mr-2 md:mr-0" />
                  </li>
                  <li className="flex items-center md:justify-end">
                    <span>Valutazione delle potenzialità dell'immobile</span>
                    <CheckCircle2 className="md:order-1 h-5 w-5 text-secondary ml-0 md:ml-2 mr-2 md:mr-0" />
                  </li>
                </ul>
              </div>
              
              <div className="relative order-1 md:order-2">
                {/* Timeline dot */}
                <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-12 h-12 bg-primary rounded-full flex items-center justify-center z-10 hidden md:flex">
                  <PieChart className="h-6 w-6 text-white" />
                </div>
                
                <img 
                  src="/images/agenzia_immobiliare_scandicci1.jpeg" 
                  alt="Consulenza immobiliare" 
                  className="rounded-xl shadow-xl w-full h-80 object-cover"
                />
              </div>
            </motion.div>
            
            {/* Step 2 */}
            <motion.div 
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="grid md:grid-cols-2 gap-16 items-center"
            >
              <div className="relative">
                {/* Timeline dot */}
                <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-12 h-12 bg-secondary rounded-full flex items-center justify-center z-10 hidden md:flex">
                  <Camera className="h-6 w-6 text-white" />
                </div>
                
                <img 
                  src="/images/agenzia_immobiliare_scandicci5.jpeg" 
                  alt="Servizio fotografico professionale" 
                  className="rounded-xl shadow-xl w-full h-80 object-cover"
                />
              </div>
              
              <div>
                <div className="inline-block bg-secondary/10 px-3 py-1 rounded text-secondary text-sm font-semibold mb-3">
                  Passo 2
                </div>
                <h3 className="text-2xl font-semibold mb-4 text-secondary">Preparazione e Marketing</h3>
                <p className="text-gray-600 text-lg mb-4">
                  Concludiamo le vendite in metà del tempo della media di mercato grazie alla nostra rete e al nostro metodo.
                </p>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-center">
                    <CheckCircle2 className="h-5 w-5 text-secondary mr-2" />
                    <span>Servizio fotografico e video professionali</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle2 className="h-5 w-5 text-secondary mr-2" />
                    <span>Home staging virtuale per valorizzare gli spazi</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle2 className="h-5 w-5 text-secondary mr-2" />
                    <span>Pubblicazione su portali immobiliari strategici</span>
                  </li>
                </ul>
              </div>
            </motion.div>
            
            {/* Step 3 */}
            <motion.div 
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="grid md:grid-cols-2 gap-16 items-center"
            >
              <div className="md:text-right order-2 md:order-1">
                <div className="inline-block bg-primary/10 px-3 py-1 rounded text-primary text-sm font-semibold mb-3">
                  Passo 3
                </div>
                <h3 className="text-2xl font-semibold mb-4 text-primary">Visite e Negoziazione</h3>
                <p className="text-gray-600 text-lg mb-4">
                  Gestiamo le visite con acquirenti selezionati e pre-qualificati, massimizzando
                  le possibilità di vendita e negoziando le migliori condizioni per te.
                </p>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-center md:justify-end">
                    <span>Presentazione professionale dell'immobile</span>
                    <CheckCircle2 className="md:order-1 h-5 w-5 text-secondary ml-0 md:ml-2 mr-2 md:mr-0" />
                  </li>
                  <li className="flex items-center md:justify-end">
                    <span>Feedback dettagliati dopo ogni visita</span>
                    <CheckCircle2 className="md:order-1 h-5 w-5 text-secondary ml-0 md:ml-2 mr-2 md:mr-0" />
                  </li>
                  <li className="flex items-center md:justify-end">
                    <span>Negoziazione esperta per il miglior prezzo</span>
                    <CheckCircle2 className="md:order-1 h-5 w-5 text-secondary ml-0 md:ml-2 mr-2 md:mr-0" />
                  </li>
                </ul>
              </div>
              
              <div className="relative order-1 md:order-2">
                {/* Timeline dot */}
                <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-12 h-12 bg-primary rounded-full flex items-center justify-center z-10 hidden md:flex">
                  <Handshake className="h-6 w-6 text-white" />
                </div>
                
                <img 
                  src="/images/agenzia_immobiliare_scandicci2.jpeg" 
                  alt="Negoziazione immobiliare" 
                  className="rounded-xl shadow-xl w-full h-80 object-cover"
                />
              </div>
            </motion.div>
            
            {/* Step 4 */}
            <motion.div 
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="grid md:grid-cols-2 gap-16 items-center"
            >
              <div className="relative">
                {/* Timeline dot */}
                <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-12 h-12 bg-secondary rounded-full flex items-center justify-center z-10 hidden md:flex">
                  <Key className="h-6 w-6 text-white" />
                </div>
                
                <img 
                  src="/images/agenzia3.jpeg" 
                  alt="Consegna chiavi" 
                  className="rounded-xl shadow-xl w-full h-80 object-cover"
                />
              </div>
              
              <div>
                <div className="inline-block bg-secondary/10 px-3 py-1 rounded text-secondary text-sm font-semibold mb-3">
                  Passo 4
                </div>
                <h3 className="text-2xl font-semibold mb-4 text-secondary">Finalizzazione e Assistenza</h3>
                <p className="text-gray-600 text-lg mb-4">
                  Ti accompagniamo in ogni aspetto legale e burocratico, dalla proposta d'acquisto
                  fino al rogito, garantendoti un'esperienza senza stress.
                </p>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-center">
                    <CheckCircle2 className="h-5 w-5 text-secondary mr-2" />
                    <span>Gestione di tutta la documentazione</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle2 className="h-5 w-5 text-secondary mr-2" />
                    <span>Coordinamento con notaio e professionisti</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle2 className="h-5 w-5 text-secondary mr-2" />
                    <span>Assistenza fino alla consegna dell'immobile</span>
                  </li>
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProcessSection;